import { DataProvider, HttpError } from '@refinedev/core';
import refineDataProvider from '@refinedev/simple-rest';
import { stringify } from 'query-string';

import { deconstructFilters } from '../deconstructFilters/deconstructFilters';
import { getCookie } from '../getCookie/getCookie';

type ApiListResponse = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  results: any[]; // TODO: Fix any https://linear.app/safi/issue/CERT-250/improve-form-type-safety
  count: number;
  next?: string;
  previous?: string;
};

type RestApiNamespaces =
  | 'root'
  | 'inspections'
  | 'deals'
  | 'materials'
  | 'trade'
  | 'accounts'
  | 'grades'
  | 'trade-signals'
  | 'transport-prices'
  | 'orders'
  | 'post-deal'
  | 'logistics';

const API_PREFIX = '/api';
export const createDataProvider = (namespace: RestApiNamespaces) => {
  const API_URL = namespace === 'root' ? API_PREFIX : `${API_PREFIX}/${namespace}`;

  const provider = refineDataProvider(API_URL);

  const dataProvider: DataProvider = {
    ...provider,
    create: async ({ resource, variables }) => {
      const response = await fetch(`${API_URL}${resource}/`, {
        body: JSON.stringify(variables),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
        method: 'POST',
      });

      if (!response.ok) {
        return Promise.reject({
          errors: await response.json(),
          message: response.statusText,
          statusCode: response.status,
        } as HttpError);
      }

      return {
        data: await response.json(),
      };
    },
    custom: async ({ url, method, payload, headers }) => {
      const response = await fetch(url, {
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          ...headers,
        },
        method: method,
      });

      if (!response.ok) {
        return Promise.reject({
          errors: await response.json(),
          message: response.statusText,
          statusCode: response.status,
        } as HttpError);
      }

      return {
        data: await response.json(),
      };
    },
    deleteOne: async ({ resource, id }) => {
      const response = await fetch(`${API_URL}${resource}/${id}/`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
        method: 'DELETE',
      });

      if (!response.ok) {
        return Promise.reject({
          errors: await response.json(),
          message: response.statusText,
          statusCode: response.status,
        } as HttpError);
      }

      let data;
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      return {
        data,
      };
    },
    getList: async ({ resource, pagination, sorters, filters }) => {
      const params = stringify({
        order: sorters?.map((sorter) => `${sorter.order === 'desc' ? '-' : ''}${sorter.field}`),
        page: pagination?.current,
        page_size: pagination?.pageSize,
        ...deconstructFilters(filters),
      });

      const url = `${API_URL}${resource}?${params}`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      });

      const data = (await response.json()) as ApiListResponse;

      return {
        data: data.results,
        total: data.count,
      };
    },
    getMany: async ({ resource, ids }) => {
      const response = await fetch(`${API_URL}${resource}${ids}/`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
        method: 'GET',
      });

      return { data: [await response.json()] };
    },
    getOne: async ({ resource, id }) => {
      const response = await fetch(`${API_URL}${resource}/${id}/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      });

      return {
        data: await response.json(),
      };
    },
    update: async ({ resource, id, variables }) => {
      const response = await fetch(`${API_URL}${resource}/${id}/`, {
        body: JSON.stringify(variables),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
        method: 'PATCH',
      });

      if (!response.ok) {
        return Promise.reject({
          errors: await response.json(),
          message: response.statusText,
          statusCode: response.status,
        } as HttpError);
      }

      return {
        data: await response.json(),
      };
    },
  };

  return dataProvider;
};
